import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Input, Spin } from "antd";
import {
  CloseOutlined,
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useAiAgentChat } from "../../hooks/api/ai-agent/hooks";
import { ChatMessage } from "../../hooks/api/ai-agent/callbacks";

interface DisplayMessage {
  role: "user" | "assistant";
  content: string;
  tool_used?: string | null;
}

const INITIAL_MSG: DisplayMessage = {
  role: "assistant",
  content:
    "Hi! I'm your ARRUK assistant. I can view jobs, clients, quotes, or create new ones.\n\nTry: \"Show recent jobs\" or \"Create a job called Fire Inspection\"",
};

const QUICK_PROMPTS = ["Show recent jobs", "List my clients", "Show draft quotes", "Create a job"];

const ChatWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<DisplayMessage[]>([INITIAL_MSG]);
  const [spin, setSpin] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);

  // Listen for header button click
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("openAiChat", handler);
    return () => window.removeEventListener("openAiChat", handler);
  }, []);

  // Rotate animation interval when closed
  useEffect(() => {
    if (open) return;
    const interval = setInterval(() => {
      setSpin(true);
      setTimeout(() => setSpin(false), 700);
    }, 3000);
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const { mutate: sendMessage, isPending } = useAiAgentChat({
    onSuccess: (res) => {
      if (res?.data?.reply) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: res.data.reply, tool_used: res.data.tool_used },
        ]);
      }
    },
    onError: (err: any) => {
      const errMsg =
        err?.response?.data?.message || err?.message || "Something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Sorry, I ran into an error: ${errMsg}` },
      ]);
    },
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isPending]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isPending) return;

    const newUserMsg: DisplayMessage = { role: "user", content: text };
    const updatedMessages = [...messages, newUserMsg];
    setMessages(updatedMessages);
    setInput("");

    const history: ChatMessage[] = updatedMessages
      .slice(-12)
      .slice(0, -1)
      .map((m) => ({ role: m.role, content: m.content }));

    sendMessage({ message: text, history });
  }, [input, isPending, messages, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    setInput(prompt);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        title="AI Assistant"
        style={{
          position: "fixed",
          bottom: 28,
          right: 28,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: open
            ? "#e6edf7"
            : "linear-gradient(135deg, var(--color-primary) 0%, #3d6fd6 100%)",
          border: "none",
          cursor: "pointer",
          boxShadow: open
            ? "0 2px 8px rgba(0,0,0,0.12)"
            : "0 4px 20px rgba(0,51,102,0.45), 0 0 0 0 rgba(0,102,204,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1001,
          transition: "all 0.25s ease",
          animation: !open ? "aiPulse 3s ease-in-out infinite" : "none",
        }}
      >
        {open ? (
          <CloseOutlined style={{ color: "var(--color-primary)", fontSize: 18 }} />
        ) : (
          <RobotOutlined
            style={{
              color: "#fff",
              fontSize: 22,
              transition: "transform 0.3s ease",
              transform: spin ? "rotate(20deg)" : "rotate(0deg)",
            }}
          />
        )}
      </button>

      {/* Pulse keyframe style */}
      <style>{`
        @keyframes aiPulse {
          0%   { box-shadow: 0 4px 20px rgba(0,51,102,0.45), 0 0 0 0 rgba(0,102,204,0.4); }
          60%  { box-shadow: 0 4px 20px rgba(0,51,102,0.45), 0 0 0 10px rgba(0,102,204,0); }
          100% { box-shadow: 0 4px 20px rgba(0,51,102,0.45), 0 0 0 0 rgba(0,102,204,0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: "fixed",
            bottom: 96,
            right: 24,
            width: 390,
            maxWidth: "calc(100vw - 32px)",
            height: 560,
            maxHeight: "calc(100vh - 120px)",
            background: "#fff",
            borderRadius: 18,
            boxShadow: "0 12px 48px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            zIndex: 1000,
            overflow: "hidden",
            border: "1px solid rgba(0,51,102,0.12)",
            animation: "slideUp 0.25s ease",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg, var(--color-primary) 0%, #3d6fd6 100%)",
              padding: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <RobotOutlined style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                ARRUK Assistant
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: "#4ade80",
                    display: "inline-block",
                    boxShadow: "0 0 4px #4ade80",
                  }}
                />
                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 11 }}>
                  Online
                </span>
              </div>
            </div>
            <button
              onClick={() => setMessages([INITIAL_MSG])}
              title="New chat"
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "none",
                cursor: "pointer",
                borderRadius: 8,
                padding: "6px 8px",
                color: "#fff",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                transition: "background 0.2s",
              }}
            >
              <ReloadOutlined />
            </button>
            <button
              onClick={() => setOpen(false)}
              title="Close"
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "none",
                cursor: "pointer",
                borderRadius: 8,
                padding: "6px 8px",
                color: "#fff",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                transition: "background 0.2s",
              }}
            >
              <CloseOutlined />
            </button>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "16px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
              background: "#f5f7fb",
            }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                  alignItems: "flex-end",
                  gap: 8,
                }}
              >
                {msg.role === "assistant" && (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, var(--color-primary) 0%, #3d6fd6 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: "0 2px 6px rgba(0,51,102,0.3)",
                    }}
                  >
                    <RobotOutlined style={{ color: "#fff", fontSize: 13 }} />
                  </div>
                )}
                <div
                  style={{
                    maxWidth: "76%",
                    padding: "10px 14px",
                    borderRadius:
                      msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    background: msg.role === "user"
                      ? "linear-gradient(135deg, var(--color-primary) 0%, #3d6fd6 100%)"
                      : "#fff",
                    color: msg.role === "user" ? "#fff" : "#1a1a2e",
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    boxShadow: msg.role === "user"
                      ? "0 3px 10px rgba(0,51,102,0.3)"
                      : "0 2px 8px rgba(0,0,0,0.07)",
                  }}
                >
                  {msg.content}
                  {msg.tool_used && (
                    <div
                      style={{
                        marginTop: 7,
                        fontSize: 10.5,
                        color: msg.role === "user" ? "rgba(255,255,255,0.65)" : "#8899aa",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      ⚡ {msg.tool_used.replace(/_/g, " ")}
                    </div>
                  )}
                </div>
                {msg.role === "user" && (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: "#dce8f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <UserOutlined style={{ color: "var(--color-primary)", fontSize: 13 }} />
                  </div>
                )}
              </div>
            ))}

            {isPending && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--color-primary) 0%, #3d6fd6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RobotOutlined style={{ color: "#fff", fontSize: 13 }} />
                </div>
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#fff",
                    borderRadius: "18px 18px 18px 4px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                    display: "flex",
                    gap: 5,
                    alignItems: "center",
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: "var(--color-primary)",
                        opacity: 0.7,
                        display: "inline-block",
                        animation: `dotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <style>{`
              @keyframes dotBounce {
                0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                40%            { transform: translateY(-6px); opacity: 1; }
              }
            `}</style>
            <div ref={bottomRef} />
          </div>

          {/* Quick prompts */}
          <div
            style={{
              padding: "8px 14px 0",
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              background: "#fff",
              borderTop: "1px solid #eef1f6",
            }}
          >
            {QUICK_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                onClick={() => handleQuickPrompt(prompt)}
                style={{
                  fontSize: 11.5,
                  padding: "4px 11px",
                  borderRadius: 14,
                  border: "1px solid #ccd9ee",
                  background: "#f0f5ff",
                  color: "var(--color-primary)",
                  cursor: "pointer",
                  marginBottom: 4,
                  fontWeight: 500,
                  transition: "all 0.15s",
                }}
              >
                {prompt}
              </button>
            ))}
          </div>

          {/* Input area */}
          <div
            style={{
              padding: "10px 14px 16px",
              background: "#fff",
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
            }}
          >
            <Input.TextArea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything or say 'create a job'…"
              autoSize={{ minRows: 1, maxRows: 4 }}
              style={{
                borderRadius: 12,
                resize: "none",
                fontSize: 13.5,
                borderColor: "#ccd9ee",
              }}
              disabled={isPending}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={isPending}
              disabled={!input.trim()}
              style={{
                borderRadius: 12,
                height: 38,
                minWidth: 38,
                background: "linear-gradient(135deg, var(--color-primary) 0%, #3d6fd6 100%)",
                border: "none",
                flexShrink: 0,
                boxShadow: "0 3px 10px rgba(0,51,102,0.3)",
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
